// FOUND-11: Phase 2 parent-origin resolver — consumes server-injected allowed origins.
// Reads <meta name="x-tenant-allowed-origins"> and cross-checks against document.referrer.
// Falls back to null (fail-closed) if meta tag is absent.

/**
 * Reads the server-injected <meta name="x-tenant-allowed-origins"> content,
 * cross-checks against document.referrer origin (case-insensitive),
 * and returns the matched allowed origin or null if no match.
 */
export function resolveTargetOrigin(): string | null {
  try {
    if (typeof document === "undefined") return null;

    // Read allowed origins from server-injected meta tag.
    const meta = document.querySelector('meta[name="x-tenant-allowed-origins"]');
    const metaContent = meta?.getAttribute("content") ?? "";

    if (!metaContent) {
      // Phase 1 fallback: if no meta tag, fail-closed (no postMessage).
      return null;
    }

    // Meta content is space-separated list of origins.
    const allowedOrigins = metaContent
      .split(/\s+/)
      .filter(Boolean)
      .map((o) => o.toLowerCase());

    if (!document.referrer) return null;

    const referrerOrigin = new URL(document.referrer).origin.toLowerCase();

    // Return the first allowed origin that matches the referrer origin.
    const match = allowedOrigins.find((o) => o === referrerOrigin);
    return match ?? null;
  } catch {
    return null;
  }
}

// Backwards-compatible alias (plan named it resolveParentOrigin).
export const resolveParentOrigin = resolveTargetOrigin;
